import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CheckCircle, LogIn, Mail, Package, Upload, UserPlus, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi, clientApi, orderApi } from '../../api/client';
import { serviceProducts, getProductPriceRange } from '../../data/serviceProducts';
import { getMainService, mainServiceGroups, mainServicesByGroup } from '../../data/mainServices';
import { interpretingServices } from '../../data/interpretingServices';
import { useSubServices, useCatalogTypes, useCatalogLanguages } from '../../data/subServicesStore';
import { flagEmoji, langFlagUrl } from '../../utils/langFlags';
import LangFlagSelect from '../common/LangFlagSelect';
import { allCountries } from '../../data/quoteFormOptions';
import { getVilleOptions, getRegionOptions } from '../../data/geoData';
import { formatTndWithEur } from '../../utils/currency';
import './QuoteRequestForm.css';

const TOTAL_STEPS = 10;

const STEP_TITLES = [
  'Type de prestation',
  'Type de service',
  'Langues',
  'Document / Mission',
  'Domaine / Mode',
  'Services & vérification',
  'Délai',
  'Livraison',
  'Informations client',
  'Récapitulatif',
];

const STEP_HINTS = [
  'Choisissez traduction ou interprétariat',
  'Précisez le type de prestation',
  'Indiquez la ou les combinaisons',
  'Décrivez le document ou la mission',
  'Domaine pour la traduction, mode pour l\'interprétariat',
  'Services officiels et vérification professionnelle',
  'Sélectionnez la cadence souhaitée',
  'Choisissez le format et le mode',
  'Vos coordonnées',
  'Vérifiez le récapitulatif et le prix avant de payer',
];

const TRANSLATION_STEP_TITLES = [
  'Prestation',
  'Type & domaine',
  'Langues',
  'Document',
  'Services & options',
  'Délai & livraison',
  'Destinataire & client',
  'Récapitulatif & paiement',
];

const TRANSLATION_STEP_HINTS = [
  'Choisissez traduction ou interprétariat',
  'Type de traduction et domaine',
  'Combinaisons de langues et précisions',
  'Type, titre et fichiers à traduire',
  'Certification, vérification et mise en page',
  'Délai, format et mode de livraison',
  'Destinataire et vos coordonnées',
  'Vérifiez le récapitulatif et le prix avant de payer',
];

const LANGUAGES = ['Français', 'Arabe', 'Anglais', 'Italien', 'Espagnol', 'Allemand', 'Autre'];

const TRANSLATION_TYPES = [
  { id: 'professional', label: 'Traduction professionnelle' },
  { id: 'official', label: 'Traduction officielle' },
];

// Types officiels = groupe « official » du services.json + certification (commande uniquement)
const OFFICIAL_TRANSLATION_TYPES = [
  ...mainServicesByGroup('official').map((s) => ({ id: s.slug, label: s.name })),
  { id: 'certificationDocs', label: 'Certification de documents' },
];

// Slugs interpreting.json → ids internes du formulaire (missions/détails)
const INTERP_FORM_ID = {
  medicalInterpreting: 'medical',
  businessInterpreting: 'business',
  remoteInterpreting: 'other',
};

// Libellés de secours pour les types hors base (domaines de commande)
const DOMAIN_EXTRA = [
  { id: 'administrative', label: 'Administratif' },
  { id: 'it', label: 'Informatique' },
  { id: 'scientific', label: 'Scientifique' },
  { id: 'education', label: 'Éducation' },
  { id: 'other', label: 'Autre' },
];

const OFFICIAL_SERVICES = OFFICIAL_TRANSLATION_TYPES;

// Groupes « Type de traduction » affichés dans le formulaire — les types
// eux-mêmes viennent de la base (collection catalogtypes, champ `group`).
const SERVICE_TYPE_GROUP_DEFS = ['professional', 'official'].map((id) => ({
  id,
  label: mainServiceGroups.find((g) => g.id === id)?.name || id,
  official: id === 'official',
}));

// Libellé de secours d'un type (slug services.json ou entrée propre à la commande)
const serviceTypeLabel = (id) =>
  getMainService(id)?.name
  || OFFICIAL_TRANSLATION_TYPES.find((t) => t.id === id)?.label
  || DOMAIN_EXTRA.find((d) => d.id === id)?.label
  || id;

const VERIFICATION_NEEDS = [
  { id: 'none', label: 'Non' },
  { id: 'lawyer', label: 'Oui — Avocat' },
  { id: 'notary', label: 'Oui — Notaire' },
  { id: 'both', label: 'Oui — Avocat et notaire' },
  { id: 'unknown', label: 'Je ne sais pas' },
];

const DEADLINES = [
  { id: 'standard', label: 'Standard' },
  { id: 'fast', label: 'Rapide' },
  { id: 'urgent', label: 'Urgent' },
];

const DELIVERY_FORMATS = [
  { id: 'pdf', label: 'PDF' },
  { id: 'word', label: 'Word' },
  { id: 'pdfWord', label: 'PDF + Word' },
  { id: 'original', label: 'Format original' },
  { id: 'other', label: 'Autre' },
];

const DELIVERY_MODES = [
  { id: 'email', label: 'Par e-mail' },
  { id: 'online', label: 'Téléchargement en ligne' },
  { id: 'pickup', label: 'Retrait sur place' },
  { id: 'post', label: 'Livraison par la Poste' },
  { id: 'courier', label: 'Livraison par coursier' },
  { id: 'express', label: 'Livraison express' },
  { id: 'other', label: 'Autre' },
];

const PHYSICAL_MODES = ['post', 'courier', 'express'];
const DELIVERY_FEES = { post: 10, courier: 15, express: 25 };

const INTERPRETING_MISSIONS = {
  general: { label: 'Type de mission', options: ['Réunion', 'Rendez-vous', 'Accompagnement', 'Administration', 'Événement', 'Autre'] },
  simultaneous: { label: 'Type d\'événement', options: ['Conférence', 'Congrès', 'Séminaire', 'Colloque', 'Réunion', 'Formation', 'Autre'] },
  consecutive: { label: 'Type de mission', options: ['Réunion', 'Rendez-vous', 'Entretien', 'Administration', 'Tribunal', 'Entreprise', 'Accompagnement', 'Autre'] },
  liaison: { label: 'Type de mission', options: ['Réunion', 'Rendez-vous', 'Visite', 'Accompagnement', 'Négociation', 'Administration', 'Entreprise', 'Autre'] },
  whispered: { label: 'Type de mission', options: ['Réunion', 'Conférence', 'Visite', 'Rendez-vous', 'Événement', 'Autre'] },
  conference: { label: 'Type d\'événement', options: ['Conférence', 'Congrès', 'Séminaire', 'Colloque', 'Forum', 'Formation', 'Autre'] },
  judicial: { label: 'Type de mission', options: ['Audience', 'Tribunal', 'Entretien juridique', 'Déposition', 'Médiation', 'Autre'] },
  medical: { label: 'Type de mission', options: ['Consultation', 'Examen', 'Hospitalisation', 'Suivi médical', 'Rendez-vous médical', 'Autre'] },
  business: { label: 'Type de mission', options: ['Réunion', 'Négociation', 'Entretien', 'Formation', 'Présentation', 'Visite d\'entreprise', 'Déplacement professionnel', 'Autre'] },
  other: { label: 'Type de mission', options: ['Réunion', 'Rendez-vous', 'Visite', 'Événement', 'Autre'] },
};

// Types d'interprétariat : config/interpreting.json (source unique)
const interpretingTypeOptions = (() => {
  const opts = interpretingServices.map((s) => ({
    id: INTERP_FORM_ID[s.slug] || (INTERPRETING_MISSIONS[s.slug] ? s.slug : 'other'),
    label: s.name,
  }));
  const seen = new Set();
  const deduped = opts.filter((o) => !seen.has(o.id) && seen.add(o.id));
  if (!deduped.some((o) => o.id === 'other')) deduped.push({ id: 'other', label: 'Autre' });
  return deduped;
})();

const INTERPRETING_DETAILS = {
  simultaneous: { equipment: true, program: true },
  conference: { equipment: true, program: true, speakers: true },
  judicial: { institution: 'Tribunal / institution', caseNumber: true, domain: 'Domaine juridique' },
  medical: { institution: 'Hôpital / clinique / établissement', domain: 'Domaine médical, si connu' },
  business: { institution: 'Nom de l\'entreprise', domain: 'Domaine d\'activité', program: true },
};

const INTERPRETING_MODES = [
  { id: 'onsite', label: 'En présentiel' },
  { id: 'remote', label: 'À distance' },
  { id: 'phone', label: 'Par téléphone' },
];

const CONNECTION_TYPES = [
  { id: 'video', label: 'Visioconférence' },
  { id: 'other', label: 'Autre' },
];

const EQUIPMENT_OPTIONS = [
  { id: 'available', label: 'Équipement disponible sur place' },
  { id: 'needed', label: 'Équipement à prévoir' },
  { id: 'unknown', label: 'Je ne sais pas' },
];

const PLATFORMS = [
  { id: 'zoom', label: 'Zoom' },
  { id: 'meet', label: 'Google Meet' },
  { id: 'teams', label: 'Microsoft Teams' },
  { id: 'other', label: 'Autre' },
];

const DOCUMENT_PRODUCTS = {
  'Acte de naissance': 'birth',
  'Acte de mariage': 'marriage',
  'Passeport': 'passport',
  'Document d\'identité': 'idcard',
  'Carte d\'identité': 'idcard',
  'Diplôme': 'diploma',
  'Diplôme universitaire': 'diploma',
  'Relevé de notes': 'transcript',
  'Bulletin scolaire': 'transcript',
  'Casier judiciaire': 'criminal',
  'Permis de conduire': 'driving',
  'Contrat': 'workContract',
  'Contrat commercial': 'commercialTranslation',
  'Jugement': 'judgment',
  'Décision judiciaire': 'judgment',
  'Rapport médical': 'medicalCert',
  'Certificat médical': 'medicalCert',
  'Site Internet': 'technicalWebsite',
  'Article scientifique': 'academicScientific',
  'Publication scientifique': 'academicScientific',
  'Thèse': 'academicScientific',
  'Mémoire': 'academicScientific',
};

const emptyForm = () => ({
  category: 'translation',
  translationType: '',
  officialTranslationType: '',
  specialty: '',
  docTitle: '',
  interpretingType: '',
  langPairs: [{ source: 'Français', target: 'Arabe' }],
  documentType: '',
  docOther: false,
  domain: '',
  numDocuments: '1',
  pages: '',
  words: '',
  files: [],
  officialServices: [],
  needsVerification: 'none',
  deadline: 'standard',
  deliveryDate: '',
  deliveryTime: '',
  deliveryFormat: '',
  deliveryMode: 'email',
  recipientName: '',
  recipientPhone: '',
  recipientCountry: '',
  recipientCity: '',
  recipientAddress: '',
  recipientPostal: '',
  missionType: '',
  domainInterpreting: '',
  missionDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  duration: '',
  multiDay: 'no',
  dailySchedule: '',
  interpretingMode: 'onsite',
  missionCountry: '',
  missionCity: '',
  missionRegion: '',
  address: '',
  connectionType: 'video',
  platform: '',
  meetingLink: '',
  missionPhone: '',
  phoneCountryCode: '',
  phoneNotes: '',
  missionDescription: '',
  equipment: '',
  institution: '',
  caseNumber: '',
  programFiles: [],
  speakerFiles: [],
  prepFiles: [],
  prepInstructions: '',
  projectName: '',
  instructions: '',
  additionalInfo: '',
  civility: '',
  lastName: '',
  firstName: '',
  company: '',
  role: '',
  email: '',
  phone: '',
  country: '',
  city: '',
  region: '',
});

function getProductById(id) {
  return serviceProducts.find((p) => p.id === id) || null;
}

function computeDurationHours(f) {
  if (!f.startTime || !f.endTime) return '';
  const [sh, sm] = f.startTime.split(':').map(Number);
  const [eh, em] = f.endTime.split(':').map(Number);
  const perDay = (eh * 60 + em - sh * 60 - sm) / 60;
  if (!(perDay > 0)) return '';
  let days = 1;
  if (f.multiDay === 'yes' && f.missionDate && f.endDate) {
    days = Math.max(1, Math.round((new Date(f.endDate) - new Date(f.missionDate)) / 86400000) + 1);
  }
  return String(Math.round(perDay * days * 10) / 10);
}

function calculateOrderPrice(form) {
  const breakdown = [];
  let total = 0;

  if (form.category === 'translation' && !form.documentType) {
    return { breakdown, total };
  }
  if (form.category === 'interpreting' && (!form.interpretingType || !form.missionType)) {
    return { breakdown, total };
  }

  if (form.category === 'translation') {
    const productId = DOCUMENT_PRODUCTS[form.documentType] || 'attestation';
    const product = getProductById(productId);
    const { min } = getProductPriceRange(product || { price: 40 });
    const quantity = Math.max(1, Number(form.numDocuments) || 1) * Math.max(1, Number(form.pages) || 1);
    const base = min * quantity;
    breakdown.push({ label: 'Traduction', value: base });
    total += base;

    const officialFees = { certified: 10, sworn: 20, legalization: 15, apostille: 12, certificationDocs: 15 };
    let officialTotal = form.officialServices.reduce((sum, s) => sum + (officialFees[s] || 0), 0);
    if (form.translationType === 'official' && form.officialTranslationType && !form.officialServices.includes(form.officialTranslationType)) {
      officialTotal += officialFees[form.officialTranslationType] || 0;
    }
    if (officialTotal) {
      breakdown.push({ label: 'Services officiels / options', value: officialTotal });
      total += officialTotal;
    }

    const verificationFees = { lawyer: 50, notary: 60, both: 110, unknown: 55 };
    const verificationFee = verificationFees[form.needsVerification] || 0;
    if (verificationFee) {
      breakdown.push({ label: 'Vérification professionnelle', value: verificationFee });
      total += verificationFee;
    }
  }

  if (form.category === 'interpreting') {
    const hourlyRate = 120;
    const hours = Number(form.duration) || 1;
    const base = hourlyRate * hours;
    breakdown.push({ label: 'Interprétariat', value: base });
    total += base;

    if (form.interpretingMode === 'onsite') {
      const travel = 25;
      breakdown.push({ label: 'Déplacement', value: travel });
      total += travel;
    }
  }

  const urgencyMultipliers = { standard: 0, fast: 0.3, urgent: 0.6 };
  const urgencyExtra = Math.round(total * (urgencyMultipliers[form.deadline] || 0));
  if (urgencyExtra) {
    breakdown.push({ label: 'Délai urgent / rapide', value: urgencyExtra });
    total += urgencyExtra;
  }

  const deliveryFee = DELIVERY_FEES[form.deliveryMode] || 0;
  if (deliveryFee) {
    breakdown.push({ label: 'Livraison', value: deliveryFee });
    total += deliveryFee;
  }

  return { breakdown, total };
}

function fileExt(name) {
  const parts = String(name || '').split('.');
  return parts.length > 1 ? parts.pop().toUpperCase() : '';
}

function formatSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function Field({ label, required, full, children }) {
  return (
    <div className={`qf-field${full ? ' qf-field--full' : ''}`}>
      <label className="qf-label">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
    </div>
  );
}

function Choice({ name, value, current, onChange, label }) {
  return (
    <label className={`qf-choice${current === value ? ' active' : ''}`}>
      <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} />
      {label}
    </label>
  );
}

export default function QuoteRequestForm() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm());
  const [guestMode, setGuestMode] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [claimSending, setClaimSending] = useState(false);
  const [claimSent, setClaimSent] = useState(false);
  const [error, setError] = useState('');

  const set = (patch) => setForm((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));

  // Types de traduction depuis la base (catalogtypes) + « Autre » en fin de liste pro
  const catalogTypes = useCatalogTypes();
  const catalogLangs = useCatalogLanguages();
  // Langues actives du catalogue — repli sur la liste statique hors-ligne
  const langOptions = catalogLangs.languages.length
    ? catalogLangs.languages.map((l) => ({
        value: l.nameFr || l.code,
        label: [flagEmoji(l.flag), l.nameFr || l.code].filter(Boolean).join(' '),
      }))
    : LANGUAGES.map((n) => ({ value: n, label: n }));
  // Métadonnées d'une langue (drapeau image + nom) — valeurs = nameFr || code
  const langMeta = (value) => {
    const found = catalogLangs.languages.find((l) => (l.nameFr || l.code) === value);
    return {
      name: found?.nameFr || found?.code || value,
      flagUrl: langFlagUrl(found),
      emoji: flagEmoji(found?.flag),
    };
  };
  const serviceTypeGroups = useMemo(() => SERVICE_TYPE_GROUP_DEFS.map((g) => ({
    ...g,
    types: catalogTypes.types.filter((t) => t.group === g.id).map((t) => ({ id: t.slug, label: t.titleFr || t.slug })),
  })), [catalogTypes.types]);
  const typeLabel = (id) => catalogTypes.types.find((t) => t.slug === id)?.titleFr || serviceTypeLabel(id);

  // Type de traduction sélectionné (slug en base) — les sous-documents viennent de la base
  const selectedServiceType = !form.translationType ? ''
    : form.translationType === 'official' ? form.officialTranslationType : form.domain;
  const onServiceTypeSelect = (slug, official) => set(official
    ? { translationType: 'official', officialTranslationType: slug, officialServices: [slug], domain: '', documentType: '', docOther: false }
    : { translationType: 'professional', domain: slug, officialTranslationType: '', officialServices: [], documentType: '', docOther: false });

  // Sous-services dynamiques (types de document) depuis l'API — parent = slug du service
  const docParentSlug = form.category !== 'translation' ? ''
    : form.translationType === 'official' ? form.officialTranslationType
    : form.translationType === 'professional' ? form.domain
    : '';
  const subDoc = useSubServices(docParentSlug);
  const subDocNames = useMemo(() => subDoc.items.map((s) => s.titleFr || s.slug), [subDoc.items]);



  const price = useMemo(() => calculateOrderPrice(form), [form]);

  // Cascade Pays → Ville → Région pour la mission en présentiel
  const missionCountryCode = useMemo(
    () => allCountries.find((c) => c.name === form.missionCountry)?.code || '',
    [form.missionCountry]
  );
  const missionVilleOptions = useMemo(() => getVilleOptions(missionCountryCode), [missionCountryCode]);
  const missionRegionOptions = useMemo(
    () => getRegionOptions(missionCountryCode, form.missionCity),
    [missionCountryCode, form.missionCity]
  );

  // Cascade Pays → Ville pour le client sans compte
  const clientCountryCode = useMemo(
    () => allCountries.find((c) => c.name === form.country)?.code || '',
    [form.country]
  );
  const clientVilleOptions = useMemo(() => getVilleOptions(clientCountryCode), [clientCountryCode]);
  const clientRegionOptions = useMemo(
    () => getRegionOptions(clientCountryCode, form.city),
    [clientCountryCode, form.city]
  );

  // Cascade Pays → Ville pour le destinataire (livraison physique)
  const recipientCountryCode = useMemo(
    () => allCountries.find((c) => c.name === form.recipientCountry)?.code || '',
    [form.recipientCountry]
  );
  const recipientVilleOptions = useMemo(() => getVilleOptions(recipientCountryCode), [recipientCountryCode]);



  useEffect(() => {
    if (!user) return;
    const parts = String(user.name || '').trim().split(/\s+/);
    set((prev) => ({
      ...prev,
      civility: prev.civility || user.civility || '',
      firstName: prev.firstName || parts[0] || '',
      lastName: prev.lastName || parts.slice(1).join(' ') || '',
      email: prev.email || user.email || '',
      phone: prev.phone || user.phone || '',
      country: prev.country || user.country || '',
      city: prev.city || user.city || '',
      region: prev.region || user.region || '',
    }));
  }, [user]);

  const toggleArray = (key, value) => {
    set((prev) => {
      const list = prev[key] || [];
      return { ...prev, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });
  };

  const handleFiles = (e, key) => {
    const next = [...form[key], ...Array.from(e.target.files || [])];
    set({ [key]: next });
    e.target.value = '';
  };

  const removeFile = (key, i) => set({ [key]: form[key].filter((_, idx) => idx !== i) });

  const setSchedule = (patch) => set((prev) => {
    const next = { ...prev, ...patch };
    next.duration = computeDurationHours(next);
    return next;
  });

  const updateLangPair = (i, key, value) => {
    set({
      langPairs: form.langPairs.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)),
    });
  };

  const handleDrop = (e, key) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) set({ [key]: [...form[key], ...dropped] });
  };

  const renderClientFields = (showGeo) => (
    <>
      <Field label="Civilité">
        <select value={form.civility} onChange={(e) => set({ civility: e.target.value })}>
          <option value="">Choisir…</option>
          <option value="M.">M.</option>
          <option value="Mme">Mme</option>
        </select>
      </Field>
      <Field label="Prénom" required>
        <input value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} autoComplete="given-name" />
      </Field>
      <Field label="Nom" required>
        <input value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} autoComplete="family-name" />
      </Field>
      <Field label="E-mail" required>
        <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} autoComplete="email" />
      </Field>
      <Field label="Téléphone" required>
        <input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} autoComplete="tel" />
      </Field>
      <Field label="Entreprise / Organisation">
        <input value={form.company} onChange={(e) => set({ company: e.target.value })} />
      </Field>
      {showGeo && (
        <>
          <Field label="Pays" required>
            <select value={form.country} onChange={(e) => set({ country: e.target.value, city: '', region: '' })}>
              <option value="">Choisir…</option>
              {allCountries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Ville" required>
            {!clientCountryCode ? (
              <select disabled value=""><option value="">Choisir un pays d&apos;abord…</option></select>
            ) : clientVilleOptions.length ? (
              <select value={form.city} onChange={(e) => set({ city: e.target.value, region: '' })}>
                <option value="">Choisir…</option>
                {clientVilleOptions.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                {form.city && !clientVilleOptions.some((v) => v.name === form.city) && <option value={form.city}>{form.city}</option>}
              </select>
            ) : (
              <input value={form.city} onChange={(e) => set({ city: e.target.value, region: '' })} />
            )}
          </Field>
          <Field label="Région">
            {!form.city ? (
              <select disabled value=""><option value="">Choisir une ville d&apos;abord…</option></select>
            ) : clientRegionOptions.length ? (
              <select value={form.region} onChange={(e) => set({ region: e.target.value })}>
                <option value="">Choisir…</option>
                {clientRegionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                {form.region && !clientRegionOptions.includes(form.region) && <option value={form.region}>{form.region}</option>}
              </select>
            ) : (
              <input value={form.region} onChange={(e) => set({ region: e.target.value })} />
            )}
          </Field>
        </>
      )}
    </>
  );

  const validate = () => {
    setError('');

    if (form.category === 'translation') {
      if (step === 1 && !form.category) return setError('Veuillez choisir un type de prestation.');
      if (step === 2) {
        if (catalogTypes.offline || (docParentSlug && subDoc.offline)) {
          return setError('La commande en ligne est temporairement indisponible. Veuillez réessayer ultérieurement.');
        }
        if (!form.translationType) return setError('Veuillez choisir un type de traduction.');
        if (form.translationType === 'professional' && !form.domain) {
          return setError('Veuillez choisir un type de traduction.');
        }
        if (form.translationType === 'official' && !form.officialTranslationType) {
          return setError('Veuillez choisir un type de traduction officielle.');
        }
        if (!form.documentType) return setError('Veuillez préciser le type de document.');
      }
      if (step === 3 && form.langPairs.some((p) => !p.source || !p.target)) {
        return setError('Veuillez remplir toutes les combinaisons de langues.');
      }
      if (step === 4) {
        if (!(Number(form.numDocuments) >= 1)) return setError('Veuillez indiquer le nombre de documents.');
        if (!form.files.length) return setError('Veuillez ajouter au moins un document à traduire.');
      }
      if (step === 6) {
        if (!form.deliveryDate) return setError('Veuillez choisir la date souhaitée de livraison.');
        if (!form.deliveryFormat) return setError('Veuillez choisir le format de livraison.');
        if (!form.deliveryMode) return setError('Veuillez choisir le mode de livraison.');
      }
      if (step === 7) {
        if (PHYSICAL_MODES.includes(form.deliveryMode)) {
          if (!form.recipientName || !form.recipientPhone || !form.recipientCountry || !form.recipientCity || !form.recipientAddress) {
            return setError('Veuillez renseigner les informations du destinataire (nom, téléphone, pays, ville, adresse).');
          }
        }
        if (!form.firstName || !form.lastName || !form.email || !form.phone) {
          return setError('Veuillez renseigner vos coordonnées.');
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
          return setError('Veuillez saisir une adresse e-mail valide.');
        }
        if (!form.country || !form.city) {
          return setError('Veuillez renseigner votre pays et votre ville.');
        }
      }
      return true;
    }

    if (step === 1 && !form.category) return setError('Veuillez choisir un type de prestation.');
    if (step === 2 && !form.interpretingType) {
      return setError('Veuillez choisir un type d\'interprétariat.');
    }
    if (step === 3 && form.langPairs.some((p) => !p.source || !p.target)) {
      return setError('Veuillez remplir toutes les combinaisons de langues.');
    }
    if (step === 2 && !form.missionType) {
      return setError('Veuillez préciser le type de mission.');
    }
    if (step === 5) {
      if (!form.missionDate || !form.startTime || !form.endTime) {
        return setError('Veuillez renseigner la date et les horaires de la mission.');
      }
      if (!form.duration) {
        return setError('L\'heure de fin doit être après l\'heure de début.');
      }
      if (form.multiDay === 'yes' && !form.endDate) {
        return setError('Veuillez renseigner la date de fin de la mission.');
      }
    }
    if (step === 6) {
      if (!form.interpretingMode) {
        return setError('Veuillez préciser le mode d\'interprétariat.');
      }
      if (form.interpretingMode === 'onsite' && (!form.missionCountry || !form.missionCity || !form.address)) {
        return setError('Veuillez renseigner le lieu de la mission (pays, ville, adresse).');
      }
      if (form.interpretingMode === 'remote' && !form.platform) {
        return setError('Veuillez choisir une plateforme.');
      }
      if (form.interpretingMode === 'phone' && !form.missionPhone) {
        return setError('Veuillez renseigner le numéro de téléphone.');
      }
    }
    if (step === 7 && form.interpretingType === 'other' && !form.missionDescription.trim()) {
      return setError('Veuillez décrire votre besoin.');
    }
    if (step === 9) {
      if (!form.civility || !form.firstName || !form.lastName || !form.email || !form.phone) {
        return setError('Veuillez renseigner vos coordonnées.');
      }
      if (!/^\S+@\S+\.\S+$/.test(form.email)) {
        return setError('Veuillez saisir une adresse e-mail valide.');
      }
      if (!isAuthenticated && (!form.country || !form.city)) {
        return setError('Veuillez renseigner votre pays et votre ville.');
      }
    }
    return true;
  };

  const skippedSteps = form.category === 'interpreting' ? [4] : [];
  const totalSteps = form.category === 'translation' ? 8 : TOTAL_STEPS;

  const goNext = () => {
    if (!validate()) return;
    setStep((s) => {
      let n = s + 1;
      while (skippedSteps.includes(n)) n += 1;
      return Math.min(totalSteps, n);
    });
  };

  const goPrev = () => {
    setError('');
    setStep((s) => {
      let n = s - 1;
      while (skippedSteps.includes(n)) n -= 1;
      return Math.max(1, n);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError('');

    const pairs = form.langPairs.filter((p) => p.source && p.target);
    const payload = {
      document: form.docTitle || form.projectName || (form.category === 'translation' ? 'Demande de traduction' : 'Demande d\'interprétariat'),
      category: form.category,
      productId: '',
      langPairs: pairs,
      sourceLang: pairs[0]?.source || '',
      targetLang: pairs[0]?.target || '',
      price: price.total,
      currency: 'TND',
      deadline: form.deadline,
      deadlineDate: form.deliveryDate || '',
      deliveryTime: form.deliveryTime || '',
      delivery: form.deliveryMode,
      deliveryFormat: form.deliveryFormat,
      deliveryRecipient: PHYSICAL_MODES.includes(form.deliveryMode) ? {
        name: form.recipientName,
        phone: form.recipientPhone,
        country: form.recipientCountry,
        city: form.recipientCity,
        address: form.recipientAddress,
        postalCode: form.recipientPostal,
      } : null,
      comments: [form.specialty, form.instructions, form.additionalInfo, form.prepInstructions].filter(Boolean).join('\n---\n'),
      civility: form.civility,
      lastName: form.lastName,
      firstName: form.firstName,
      email: form.email,
      phone: form.phone,
      country: form.country,
      city: form.city,
      region: form.region,
      company: form.company,
      domain: form.domain || form.domainInterpreting,
      details: {
        ...form,
        langPairs: pairs,
        pricingBreakdown: price.breakdown,
        estimatedPrice: price.total,
      },
      fileNames: [...form.files, ...form.prepFiles, ...form.programFiles, ...form.speakerFiles].map((f) => f.name),
    };

    const allFiles = [...form.files, ...form.prepFiles, ...form.programFiles, ...form.speakerFiles];

    try {
      const res = isAuthenticated
        ? await clientApi.createOrder(payload, allFiles)
        : await orderApi.create(payload, allFiles);
      const created = res.order || null;
      setSubmittedOrder(created ? {
        ...created,
        trackingToken: res.trackingToken || '',
        trackingUrl: res.trackingUrl || '',
        guest: Boolean(res.guest),
      } : null);
    } catch (err) {
      const code = err.data?.error;
      if (code === 'emailHasAccount') {
        setError('Cette adresse e-mail est liée à un compte existant. Connectez-vous pour commander.');
      } else {
        setError('Erreur lors de l\'envoi de la demande. Veuillez réessayer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClaimAccount = async () => {
    const email = form.email || submittedOrder?.contact?.email || '';
    if (!email || claimSending || claimSent) return;
    setClaimSending(true);
    try {
      await authApi.claim(email);
      setClaimSent(true);
    } catch {
      setClaimSent(true);
    } finally {
      setClaimSending(false);
    }
  };

  if (submittedOrder) {
    const orderCode = submittedOrder.id;
    const orderEmail = submittedOrder.contact?.email || form.email || user?.email || '';
    const trackUrl = `/suivi?code=${encodeURIComponent(orderCode)}&token=${encodeURIComponent(submittedOrder.trackingToken || '')}`;
    const orderDate = submittedOrder.date
      || (submittedOrder.createdAt ? new Date(submittedOrder.createdAt).toLocaleDateString('fr-FR') : '');

    return (
      <div className="qf-success card" style={{ textAlign: 'center' }}>
        <div className="qf-success-icon">
          <CheckCircle size={40} />
        </div>
        <h2>Commande {orderCode} créée</h2>
        <p>
          Votre commande est enregistrée. Nous recherchons un professionnel compatible —
          vous pourrez régler <strong>{formatTndWithEur(submittedOrder.price)}</strong> dès
          qu&apos;un traducteur aura accepté votre commande.
        </p>
        {submittedOrder.guest && (
          <p className="qf-track-hint" style={{ marginTop: 0 }}>
            Vous avez commandé sans compte. Un email avec votre lien de suivi sécurisé a été envoyé{orderEmail ? ` à ${orderEmail}` : ''}.
          </p>
        )}
        <div className="qf-success-actions">
          {submittedOrder.guest ? (
            <>
              <Link to={trackUrl} className="btn btn-primary"><Package size={16} /> Suivre ma commande</Link>
              {claimSent ? (
                <span className="qf-claim-sent"><Mail size={16} /> Email d&apos;activation envoyé — vérifiez votre boîte.</span>
              ) : (
                <button type="button" className="btn btn-outline" onClick={handleClaimAccount} disabled={claimSending}>
                  <UserPlus size={16} /> {claimSending ? 'Envoi…' : 'Créer mon compte'}
                </button>
              )}
            </>
          ) : (
            <>
              <Link to={`/app/orders/${submittedOrder.id}`} className="btn btn-primary">Voir ma commande</Link>
              <Link to="/" className="btn btn-outline">Retour à l&apos;accueil</Link>
            </>
          )}
        </div>
        {submittedOrder.guest && (
          <p className="qf-track-hint">
            Conservez votre lien de suivi sécurisé :{' '}
            <Link to={trackUrl} className="dash-link">{trackUrl}</Link>
          </p>
        )}
      </div>
    );
  }

  // Étape 1 — Accès : visiteur non connecté
  if (!isAuthenticated && !guestMode) {
    return (
      <div className="qf-gate card">
        <h2>Comment souhaitez-vous commander ?</h2>
        <p>Connectez-vous, créez un compte, ou continuez sans compte — le suivi se fera par e-mail.</p>
        <div className="qf-gate-actions">
          <button type="button" className="qf-gate-option" onClick={() => navigate('/login', { state: { from: '/order' } })}>
            <LogIn size={22} />
            <strong>Se connecter</strong>
            <small>J&apos;ai déjà un compte Tarjemlek</small>
            <small className="qf-gate-desc">Suivez et gérez vos commandes depuis votre espace client.</small>
          </button>
          <button type="button" className="qf-gate-option" onClick={() => navigate('/register', { state: { from: '/order' } })}>
            <UserPlus size={22} />
            <strong>Créer un compte</strong>
            <small>Je souhaite créer un compte</small>
            <small className="qf-gate-desc">Accédez à votre espace client et retrouvez toutes vos commandes.</small>
          </button>
          <button type="button" className="qf-gate-option" onClick={() => setGuestMode(true)}>
            <Package size={22} />
            <strong>Continuer sans compte</strong>
            <small>Commande rapide</small>
            <small className="qf-gate-desc">Aucun mot de passe requis. Le suivi se fera par e-mail.</small>
          </button>
        </div>
        <p className="qf-gate-note">Aucun mot de passe n&apos;est demandé pour une commande sans compte.</p>
      </div>
    );
  }

  const activeStepTitles = form.category === 'translation' ? TRANSLATION_STEP_TITLES : STEP_TITLES;
  const documentTypeOptions = subDocNames;

  const typeDetails = INTERPRETING_DETAILS[form.interpretingType] || {};
  const modeSectionTitle = { onsite: 'Lieu de la mission', remote: 'Connexion à distance', phone: 'Appel téléphonique' }[form.interpretingMode] || 'Mode de réalisation';
  const missionPlace = form.interpretingMode === 'onsite'
    ? [form.missionRegion || form.missionCity, form.missionCountry].filter(Boolean).join(', ') || '—'
    : form.interpretingMode === 'phone'
      ? form.missionPhone || 'Par téléphone'
      : PLATFORMS.find((p) => p.id === form.platform)?.label || 'À distance';

  const visibleSteps = activeStepTitles.map((_, i) => i + 1).filter((s) => !skippedSteps.includes(s));
  const displayStep = (s) => visibleSteps.indexOf(s) + 1;
  const progress = (displayStep(step) / visibleSteps.length) * 100;

  const stepTitle = (s) => {
    if (form.category === 'translation') return TRANSLATION_STEP_TITLES[s - 1];
    const titles = {
      2: 'Type d\'interprétariat',
      3: 'Langues',
      4: INTERPRETING_MISSIONS[form.interpretingType]?.label || 'Type de mission',
      5: 'Date et horaires',
      6: 'Mode de réalisation',
      7: 'Informations spécifiques',
      8: 'Préparation de la mission',
      9: 'Informations client',
      10: 'Récapitulatif et prix',
    };
    return titles[s] || STEP_TITLES[s - 1];
  };
  const stepHint = (s) => {
    if (form.category === 'translation') return TRANSLATION_STEP_HINTS[s - 1];
    const hints = {
      2: 'Choisissez le type d\'interprétariat',
      3: 'Indiquez la ou les combinaisons',
      4: 'S\'adapte au type d\'interprétariat choisi',
      5: 'Planification de la mission',
      6: 'En présentiel, à distance ou par téléphone',
      7: 'Champs selon le type choisi',
      8: 'Documents et instructions utiles',
      9: 'Vos coordonnées',
      10: 'Vérifiez le récapitulatif et le prix avant de payer',
    };
    return hints[s] || STEP_HINTS[s - 1];
  };

  return (
    <div className="qf-shell">
      <aside className="qf-side">
        <nav className="qf-side-card" aria-label="Étapes">
          <p className="qf-side-title">Votre demande</p>
          <ol className="qf-steps">
            {activeStepTitles.map((_, i) => {
              const s = i + 1;
              if (skippedSteps.includes(s)) return null;
              return (
                <li key={s} className={`qf-steps-item${step > s ? ' done' : ''}${step === s ? ' current' : ''}`}>
                  <button type="button" onClick={() => setStep(s)}>
                    <span className="qf-steps-num">{step > s ? <Check size={14} /> : displayStep(s)}</span>
                    <span className="qf-steps-text">
                      <strong>{stepTitle(s)}</strong>
                      <small>{stepHint(s)}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="qf-price-card">
          <p className="qf-price-title">Estimation du prix</p>
          {price.total > 0 ? (
            <>
              <dl className="qf-price-list">
                {price.breakdown.map((item, i) => (
                  <div key={i}>
                    <dt>{item.label}</dt>
                    <dd>{formatTndWithEur(item.value, 'fr-FR', 'TND')}</dd>
                  </div>
                ))}
              </dl>
              <div className="qf-price-total">
                <span>Total estimé</span>
                <strong>{formatTndWithEur(price.total, 'fr-FR', 'TND')}</strong>
              </div>
              <p className="qf-price-payment" style={{ marginTop: 10 }}>
                Le prix définitif est validé par l'admin après analyse.
              </p>
            </>
          ) : (
            <p className="qf-price-empty">Sélectionnez un type de document ou de mission pour afficher le prix.</p>
          )}
        </div>

        <div className="qf-help">
          <p className="qf-help-title">Besoin d&apos;aide ?</p>
          <p className="qf-help-text">Notre équipe vous accompagne pour préparer votre demande.</p>
        </div>
      </aside>

      <form className={'qf' + (error ? ' form-attempted' : '')} onSubmit={(e) => e.preventDefault()} noValidate>
        <header className="qf-head">
          <span className="qf-eyebrow">Étape {displayStep(step)} / {visibleSteps.length}</span>
          <h2>{stepTitle(step)}</h2>
          <p>{stepHint(step)}</p>
          <div className="qf-bar" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>
        </header>

        <div className="qf-body">
          {error && <p className="qf-error" role="alert">{error}</p>}

          {step === 1 && (
            <div className="qf-stack">
              <Field label="Que souhaitez-vous commander ?" required>
                <div className="qf-radios">
                  <Choice name="category" value="translation" current={form.category} onChange={(v) => set({ category: v, translationType: '', interpretingType: '' })} label="Traduction" />
                  <Choice name="category" value="interpreting" current={form.category} onChange={(v) => set({ category: v, translationType: '', interpretingType: '' })} label="Interprétariat" />
                </div>
              </Field>
            </div>
          )}

          {step === 2 && form.category === 'translation' && (
            <div className="qf-stack">
              <div className="qf-grid">
                <Field label="Type de traduction" required>
                  {catalogTypes.offline ? (
                    <p className="qf-offline-note" role="alert">La commande en ligne est temporairement indisponible. Veuillez réessayer ultérieurement.</p>
                  ) : catalogTypes.loading ? (
                    <small className="qf-hint">Chargement des types de traduction…</small>
                  ) : (
                    <div className="qf-stack qf-type-groups">
                      {serviceTypeGroups.map((g) => g.types.length === 0 ? null : (
                        <div key={g.id}>
                          <p className="qf-section">{g.label}</p>
                          <div className="qf-radios qf-radios-wrap">
                            {g.types.map((t) => (
                              <Choice key={t.id} name="serviceType" value={t.id} current={selectedServiceType} onChange={(v) => onServiceTypeSelect(v, g.official)} label={t.label} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Field>
                <Field label="Type de document" required>
                  {!form.translationType ? (
                    <small className="qf-hint">Choisissez d&apos;abord un type de traduction.</small>
                  ) : subDoc.offline ? (
                    <p className="qf-offline-note" role="alert">La commande en ligne est temporairement indisponible. Veuillez réessayer ultérieurement.</p>
                  ) : subDoc.loading ? (
                    <small className="qf-hint">Chargement des types de document…</small>
                  ) : (
                    <>
                      <div className="qf-radios qf-radios-wrap qf-doc-list">
                        {documentTypeOptions.filter((t) => (t || '').toLowerCase() !== 'autre').map((t) => (
                          <Choice key={t} name="documentType" value={t} current={form.docOther ? '' : form.documentType} onChange={(v) => set({ documentType: v, docOther: false })} label={t} />
                        ))}
                        <Choice name="documentType" value="__other" current={form.docOther ? '__other' : ''} onChange={() => set({ docOther: true, documentType: '' })} label="Autre" />
                      </div>
                      {form.docOther && (
                        <input style={{ marginTop: 10 }} value={form.documentType} onChange={(e) => set({ documentType: e.target.value })} placeholder="Précisez le type de document…" />
                      )}
                    </>
                  )}
                </Field>
              </div>
            </div>
          )}

          {step === 3 && form.category === 'translation' && (
            <div className="qf-stack">
              {form.langPairs.map((p, i) => (
                <div key={i} className="qf-lang-row">
                  <Field label="Langue source" required>
                    <LangFlagSelect
                      value={p.source}
                      onChange={(v) => updateLangPair(i, 'source', v)}
                      options={langOptions.map((l) => l.value)}
                      meta={langMeta}
                      placeholder="Langue source"
                      ariaLabel="Langue source"
                    />
                  </Field>
                  <Field label="Langue cible" required>
                    <LangFlagSelect
                      value={p.target}
                      onChange={(v) => updateLangPair(i, 'target', v)}
                      options={langOptions.map((l) => l.value)}
                      meta={langMeta}
                      placeholder="Langue cible"
                      ariaLabel="Langue cible"
                    />
                  </Field>
                </div>
              ))}
              <Field label="Spécialité / précisions">
                <textarea rows={3} value={form.specialty} onChange={(e) => set({ specialty: e.target.value })} placeholder="Terminologie particulière, contexte, précisions utiles…" />
              </Field>
            </div>
          )}

          {step === 4 && form.category === 'translation' && (
            <div className="qf-stack">
              <div className="qf-grid">
                <Field label="Titre du document">
                  <input value={form.docTitle} onChange={(e) => set({ docTitle: e.target.value })} placeholder="ex. Acte de naissance" />
                </Field>
                <Field label="Nombre de documents" required>
                  <input type="number" min="1" value={form.numDocuments} onChange={(e) => set({ numDocuments: e.target.value })} />
                </Field>
                <Field label="Nombre de pages">
                  <input type="number" min="0" value={form.pages} onChange={(e) => set({ pages: e.target.value })} />
                </Field>
                <Field label="Nombre de mots">
                  <input type="number" min="0" value={form.words} onChange={(e) => set({ words: e.target.value })} />
                </Field>
              </div>
              <Field label="Documents à traduire" required>
                <label className="qf-upload" onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleDrop(e, 'files')}>
                  <Upload size={20} />
                  <span>Glissez-déposez vos fichiers ici ou cliquez pour ajouter un document</span>
                  <small>Formats acceptés : PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, JPG, JPEG, PNG</small>
                  <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png" onChange={(e) => handleFiles(e, 'files')} />
                </label>
                {form.files.length > 0 && (
                  <ul className="qf-files">
                    {form.files.map((f, i) => (
                      <li key={`${f.name}-${i}`}>
                        <span>{f.name}</span>
                        <small>{[fileExt(f.name), formatSize(f.size)].filter(Boolean).join(' — ')}</small>
                        <button type="button" onClick={() => removeFile('files', i)} aria-label="Supprimer"><X size={14} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
            </div>
          )}

          {step === 2 && form.category === 'interpreting' && (
            <div className="qf-stack">
              <Field label="Type d\'interprétariat" required>
                <div className="qf-radios qf-radios-wrap">
                  {interpretingTypeOptions.map((t) => (
                    <Choice key={t.id} name="interpretingType" value={t.id} current={form.interpretingType} onChange={(v) => set({ interpretingType: v, missionType: '', equipment: '', institution: '', caseNumber: '', domainInterpreting: '' })} label={t.label} />
                  ))}
                </div>
              </Field>
              <Field label={INTERPRETING_MISSIONS[form.interpretingType]?.label || 'Type de mission'} required>
                <div className="qf-radios qf-radios-wrap">
                  {(INTERPRETING_MISSIONS[form.interpretingType]?.options || []).map((o) => (
                    <Choice key={o} name="missionType" value={o} current={form.missionType} onChange={(v) => set({ missionType: v })} label={o} />
                  ))}
                </div>
              </Field>
            </div>
          )}

          {step === 3 && form.category === 'interpreting' && (
            <div className="qf-stack">
              {form.langPairs.map((p, i) => (
                <div key={i} className="qf-lang-row">
                  <Field label="Langue source" required>
                    <LangFlagSelect
                      value={p.source}
                      onChange={(v) => updateLangPair(i, 'source', v)}
                      options={langOptions.map((l) => l.value)}
                      meta={langMeta}
                      placeholder="Langue source"
                      ariaLabel="Langue source"
                    />
                  </Field>
                  <Field label="Langue cible" required>
                    <LangFlagSelect
                      value={p.target}
                      onChange={(v) => updateLangPair(i, 'target', v)}
                      options={langOptions.map((l) => l.value)}
                      meta={langMeta}
                      placeholder="Langue cible"
                      ariaLabel="Langue cible"
                    />
                  </Field>
                </div>
              ))}
            </div>
          )}

          {step === 5 && form.category === 'translation' && (
            <div className="qf-stack">
              <p className="qf-section">Certification / assermentation</p>
              <div className="qf-grid">
                {OFFICIAL_SERVICES.map((s) => {
                  const yes = form.officialServices.includes(s.id);
                  return (
                    <Field key={s.id} label={s.label}>
                      <div className="qf-radios">
                        <Choice name={`official_${s.id}`} value="yes" current={yes ? 'yes' : 'no'} onChange={() => !yes && toggleArray('officialServices', s.id)} label="Oui" />
                        <Choice name={`official_${s.id}`} value="no" current={yes ? 'yes' : 'no'} onChange={() => yes && toggleArray('officialServices', s.id)} label="Non" />
                      </div>
                    </Field>
                  );
                })}
              </div>

              <p className="qf-section">Vérification professionnelle</p>
              <Field label="Une vérification professionnelle est-elle nécessaire ?">
                <div className="qf-radios qf-radios-wrap">
                  {VERIFICATION_NEEDS.map((v) => (
                    <Choice key={v.id} name="needsVerification" value={v.id} current={form.needsVerification} onChange={(val) => set({ needsVerification: val })} label={v.label} />
                  ))}
                </div>
                <small className="qf-hint">Vous choisissez uniquement le type de vérification — l&apos;administrateur effectue l&apos;affectation.</small>
              </Field>
            </div>
          )}

          {step === 6 && form.category === 'translation' && (
            <div className="qf-stack">
              <p className="qf-section">Délai</p>
              <Field label="Délai souhaité" required>
                <div className="qf-radios qf-radios-wrap">
                  {DEADLINES.map((d) => (
                    <Choice key={d.id} name="deadline" value={d.id} current={form.deadline} onChange={(v) => set({ deadline: v })} label={d.label} />
                  ))}
                </div>
              </Field>
              <div className="qf-grid">
                <Field label="Date souhaitée de livraison" required>
                  <input type="date" value={form.deliveryDate} onChange={(e) => set({ deliveryDate: e.target.value })} />
                </Field>
                <Field label="Heure souhaitée">
                  <input type="time" value={form.deliveryTime} onChange={(e) => set({ deliveryTime: e.target.value })} />
                </Field>
              </div>

              <p className="qf-section">Livraison</p>
              <Field label="Format souhaité" required>
                <div className="qf-radios qf-radios-wrap">
                  {DELIVERY_FORMATS.map((f) => (
                    <Choice key={f.id} name="deliveryFormat" value={f.id} current={form.deliveryFormat} onChange={(v) => set({ deliveryFormat: v })} label={f.label} />
                  ))}
                </div>
              </Field>
              <Field label="Mode de livraison" required>
                <div className="qf-radios qf-radios-wrap">
                  {DELIVERY_MODES.map((m) => (
                    <Choice key={m.id} name="deliveryMode" value={m.id} current={form.deliveryMode} onChange={(v) => set({ deliveryMode: v })} label={m.label} />
                  ))}
                </div>
              </Field>
            </div>
          )}

          {step === 7 && form.category === 'translation' && (
            <div className="qf-stack">
              <p className="qf-section">Informations client</p>
              <div className="qf-grid">
                {renderClientFields(true)}
                <Field label="Instructions / remarques" full>
                  <textarea rows={4} value={form.instructions} onChange={(e) => set({ instructions: e.target.value })} placeholder="Indiquez toute information utile concernant votre traduction : terminologie particulière, contexte, exigences de mise en page, vocabulaire spécialisé, consignes particulières, etc." />
                </Field>
              </div>

              {PHYSICAL_MODES.includes(form.deliveryMode) && (
                <>
                  <p className="qf-section">Adresse de livraison</p>
                  <div className="qf-grid">
                    <Field label="Nom du destinataire" required>
                      <input value={form.recipientName} onChange={(e) => set({ recipientName: e.target.value })} />
                    </Field>
                    <Field label="Téléphone" required>
                      <input type="tel" value={form.recipientPhone} onChange={(e) => set({ recipientPhone: e.target.value })} />
                    </Field>
                    <Field label="Pays" required>
                      <select value={form.recipientCountry} onChange={(e) => set({ recipientCountry: e.target.value, recipientCity: '' })}>
                        <option value="">Choisir…</option>
                        {allCountries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Ville" required>
                      {!recipientCountryCode ? (
                        <select disabled value=""><option value="">Choisir un pays d&apos;abord…</option></select>
                      ) : recipientVilleOptions.length ? (
                        <select value={form.recipientCity} onChange={(e) => set({ recipientCity: e.target.value })}>
                          <option value="">Choisir…</option>
                          {recipientVilleOptions.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                          {form.recipientCity && !recipientVilleOptions.some((v) => v.name === form.recipientCity) && <option value={form.recipientCity}>{form.recipientCity}</option>}
                        </select>
                      ) : (
                        <input value={form.recipientCity} onChange={(e) => set({ recipientCity: e.target.value })} />
                      )}
                    </Field>
                    <Field label="Adresse complète" required full>
                      <input value={form.recipientAddress} onChange={(e) => set({ recipientAddress: e.target.value })} placeholder="N°, rue, étage…" />
                    </Field>
                    <Field label="Code postal">
                      <input value={form.recipientPostal} onChange={(e) => set({ recipientPostal: e.target.value })} />
                    </Field>
                  </div>
                  <small className="qf-hint">Les frais de livraison sont ajoutés automatiquement au prix.</small>
                </>
              )}
            </div>
          )}

          {step === 7 && form.category === 'interpreting' && (
            <div className="qf-mission">
              {form.interpretingType === 'other' && (
                <Field label="Description du besoin" required>
                  <textarea rows={3} value={form.missionDescription} onChange={(e) => set({ missionDescription: e.target.value })} placeholder="Décrivez votre besoin d'interprétariat…" />
                </Field>
              )}
              {typeDetails.institution && (
                <Field label={typeDetails.institution}>
                  <input value={form.institution} onChange={(e) => set({ institution: e.target.value })} />
                </Field>
              )}
              {typeDetails.caseNumber && (
                <Field label="Numéro de dossier (si nécessaire)">
                  <input value={form.caseNumber} onChange={(e) => set({ caseNumber: e.target.value })} />
                </Field>
              )}
              {typeDetails.domain && (
                <Field label={typeDetails.domain}>
                  <input value={form.domainInterpreting} onChange={(e) => set({ domainInterpreting: e.target.value })} />
                </Field>
              )}
              {typeDetails.equipment && (
                <Field label="Équipement d'interprétation">
                  <div className="qf-radios qf-radios-wrap">
                    {EQUIPMENT_OPTIONS.map((eq) => (
                      <Choice key={eq.id} name="equipment" value={eq.id} current={form.equipment} onChange={(v) => set({ equipment: v })} label={eq.label} />
                    ))}
                  </div>
                </Field>
              )}
              {form.interpretingType !== 'other' && (
                <Field label="Objet de la mission" full>
                  <textarea rows={3} value={form.missionDescription} onChange={(e) => set({ missionDescription: e.target.value })} placeholder="Contexte, sujet, intervenants…" />
                </Field>
              )}

            </div>
          )}

          {step === 5 && form.category === 'interpreting' && (
            <div className="qf-mission">
              <Field label="Mission sur plusieurs jours ?">
                <div className="qf-radios qf-radios-wrap">
                  <Choice name="multiDay" value="no" current={form.multiDay} onChange={(v) => setSchedule({ multiDay: v, endDate: '', dailySchedule: '' })} label="Non" />
                  <Choice name="multiDay" value="yes" current={form.multiDay} onChange={(v) => setSchedule({ multiDay: v })} label="Oui" />
                </div>
              </Field>
              <Field label={form.multiDay === 'yes' ? 'Date de début' : 'Date de la mission'} required>
                <input type="date" value={form.missionDate} onChange={(e) => setSchedule({ missionDate: e.target.value })} />
              </Field>
              {form.multiDay === 'yes' && (
                <Field label="Date de fin" required>
                  <input type="date" value={form.endDate} onChange={(e) => setSchedule({ endDate: e.target.value })} />
                </Field>
              )}
              <Field label="Heure de début" required>
                <input type="time" value={form.startTime} onChange={(e) => setSchedule({ startTime: e.target.value })} />
              </Field>
              <Field label="Heure de fin" required>
                <input type="time" value={form.endTime} onChange={(e) => setSchedule({ endTime: e.target.value })} />
              </Field>
              <Field label="Durée prévue">
                <input value={form.duration ? `${form.duration} h` : ''} readOnly placeholder="—" />
              </Field>
              {form.multiDay === 'yes' && (
                <Field label="Horaires pour chaque journée">
                  <textarea rows={2} value={form.dailySchedule} onChange={(e) => set({ dailySchedule: e.target.value })} placeholder="ex. 09:00 – 17:00 chaque jour" />
                </Field>
              )}

            </div>
          )}

          {step === 6 && form.category === 'interpreting' && (
            <div className="qf-mission">
              <Field label="Mode de réalisation" required full>
                <div className="qf-radios qf-radios-wrap">
                  {INTERPRETING_MODES.map((m) => (
                    <Choice key={m.id} name="interpretingMode" value={m.id} current={form.interpretingMode} onChange={(v) => set({ interpretingMode: v, missionCountry: '', missionCity: '', missionRegion: '', address: '', connectionType: 'video', platform: '', meetingLink: '', missionPhone: '', phoneCountryCode: '', phoneNotes: '' })} label={m.label} />
                  ))}
                </div>
              </Field>
              <p className="qf-section">{modeSectionTitle}</p>
              {form.interpretingMode === 'onsite' && (
                <>
                  <Field label="Pays" required>
                    <select value={form.missionCountry} onChange={(e) => set({ missionCountry: e.target.value, missionCity: '', missionRegion: '' })}>
                      <option value="">Choisir…</option>
                      {allCountries.map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Ville" required>
                    {!missionCountryCode ? (
                      <select disabled value=""><option value="">Choisir un pays d'abord…</option></select>
                    ) : missionVilleOptions.length ? (
                      <select value={form.missionCity} onChange={(e) => set({ missionCity: e.target.value, missionRegion: '' })}>
                        <option value="">Choisir…</option>
                        {missionVilleOptions.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                        {form.missionCity && !missionVilleOptions.some((v) => v.name === form.missionCity) && (
                          <option value={form.missionCity}>{form.missionCity}</option>
                        )}
                      </select>
                    ) : (
                      <input value={form.missionCity} onChange={(e) => set({ missionCity: e.target.value })} />
                    )}
                  </Field>
                  <Field label="Région">
                    {!form.missionCity ? (
                      <select disabled value=""><option value="">Choisir une ville d'abord…</option></select>
                    ) : missionRegionOptions.length ? (
                      <select value={form.missionRegion} onChange={(e) => set({ missionRegion: e.target.value })}>
                        <option value="">Choisir…</option>
                        {missionRegionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                        {form.missionRegion && !missionRegionOptions.includes(form.missionRegion) && (
                          <option value={form.missionRegion}>{form.missionRegion}</option>
                        )}
                      </select>
                    ) : (
                      <input value={form.missionRegion} onChange={(e) => set({ missionRegion: e.target.value })} placeholder="Ville / région" />
                    )}
                  </Field>
                  <Field label="Adresse complète" required>
                    <input value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="N°, rue, établissement, étage…" />
                  </Field>
                </>
              )}
              {form.interpretingMode === 'remote' && (
                <>
                  <Field label="Type de connexion" required>
                    <div className="qf-radios qf-radios-wrap">
                      {CONNECTION_TYPES.map((c) => (
                        <Choice key={c.id} name="connectionType" value={c.id} current={form.connectionType} onChange={(v) => set({ connectionType: v })} label={c.label} />
                      ))}
                    </div>
                  </Field>
                  <Field label="Plateforme" required>
                    <select value={form.platform} onChange={(e) => set({ platform: e.target.value })}>
                      <option value="">Choisir…</option>
                      {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Lien de réunion (si disponible)">
                    <input value={form.meetingLink} onChange={(e) => set({ meetingLink: e.target.value })} placeholder="https://…" />
                  </Field>
                </>
              )}
              {form.interpretingMode === 'phone' && (
                <>
                  <Field label="Numéro de téléphone" required>
                    <input type="tel" value={form.missionPhone} onChange={(e) => set({ missionPhone: e.target.value })} />
                  </Field>
                  <Field label="Indicatif du pays">
                    <input value={form.phoneCountryCode} onChange={(e) => set({ phoneCountryCode: e.target.value })} placeholder="ex. +216" />
                  </Field>
                  <Field label="Informations complémentaires">
                    <input value={form.phoneNotes} onChange={(e) => set({ phoneNotes: e.target.value })} />
                  </Field>
                </>
              )}

            </div>
          )}

          {step === 8 && form.category === 'interpreting' && (
            <div className="qf-mission">
              {typeDetails.program && (
                <Field label="Programme / ordre du jour">
                  <label className="qf-upload">
                    <Upload size={20} />
                    <span>Ajouter le programme / ordre du jour</span>
                    <small>PDF, Word, JPG, PNG, etc.</small>
                    <input type="file" multiple onChange={(e) => handleFiles(e, 'programFiles')} />
                  </label>
                  {form.programFiles.length > 0 && (
                    <ul className="qf-files">
                      {form.programFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`}>
                          <span>{f.name}</span>
                          <button type="button" onClick={() => removeFile('programFiles', i)} aria-label="Retirer"><X size={14} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>
              )}
              {typeDetails.speakers && (
                <Field label="Documents des intervenants">
                  <label className="qf-upload">
                    <Upload size={20} />
                    <span>Ajouter les documents des intervenants</span>
                    <small>PDF, Word, JPG, PNG, etc.</small>
                    <input type="file" multiple onChange={(e) => handleFiles(e, 'speakerFiles')} />
                  </label>
                  {form.speakerFiles.length > 0 && (
                    <ul className="qf-files">
                      {form.speakerFiles.map((f, i) => (
                        <li key={`${f.name}-${i}`}>
                          <span>{f.name}</span>
                          <button type="button" onClick={() => removeFile('speakerFiles', i)} aria-label="Retirer"><X size={14} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Field>
              )}
              <Field label="Documents de préparation (facultatif)">
                <label className="qf-upload">
                  <Upload size={20} />
                  <span>Ajouter des documents de préparation</span>
                  <small>Formats acceptés : PDF, Word, JPG, PNG, etc.</small>
                  <small>Programme, ordre du jour, présentation, vocabulaire spécifique, documents à consulter, etc.</small>
                  <input type="file" multiple onChange={(e) => handleFiles(e, 'prepFiles')} />
                </label>
                {form.prepFiles.length > 0 && (
                  <ul className="qf-files">
                    {form.prepFiles.map((f, i) => (
                      <li key={`${f.name}-${i}`}>
                        <span>{f.name}</span>
                        <button type="button" onClick={() => removeFile('prepFiles', i)} aria-label="Retirer"><X size={14} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
              <Field label="Instructions particulières (facultatif)">
                <textarea rows={4} value={form.prepInstructions} onChange={(e) => set({ prepInstructions: e.target.value })} placeholder="Précisions sur la mission, consignes particulières, informations utiles pour l'interprète…" />
              </Field>
            </div>
          )}

          {step === 8 && form.category === 'translation' && (
            <div className="qf-stack">
              <p className="qf-section">Récapitulatif de la commande</p>
              <div className="qf-recap">
                <div className="qf-recap-row"><span>Type de traduction</span><strong>{form.translationType === 'official' ? `Traduction officielle — ${typeLabel(form.officialTranslationType)}` : `Traduction professionnelle — ${typeLabel(form.domain)}`}</strong></div>
                <div className="qf-recap-row"><span>Langue source</span><strong>{form.langPairs[0]?.source || '—'}</strong></div>
                <div className="qf-recap-row"><span>Langue(s) cible(s)</span><strong>{form.langPairs.map((p) => p.target).filter(Boolean).join(', ') || '—'}</strong></div>
                <div className="qf-recap-row"><span>Document(s)</span><strong>{form.documentType || '—'}{form.docTitle ? ` — ${form.docTitle}` : ''} (×{Number(form.numDocuments) || 1})</strong></div>
                <div className="qf-recap-row"><span>Nombre de pages</span><strong>{form.pages || '—'}</strong></div>
                <div className="qf-recap-row"><span>Nombre de mots</span><strong>{form.words || '—'}</strong></div>
                <div className="qf-recap-row"><span>Services sélectionnés</span><strong>{form.officialServices.map((s) => OFFICIAL_SERVICES.find((o) => o.id === s)?.label).filter(Boolean).join(', ') || 'Aucun'}</strong></div>
                <div className="qf-recap-row"><span>Vérification professionnelle</span><strong>{VERIFICATION_NEEDS.find((v) => v.id === form.needsVerification)?.label}</strong></div>
                <div className="qf-recap-row"><span>Délai</span><strong>{DEADLINES.find((d) => d.id === form.deadline)?.label}{form.deliveryDate ? ` — ${form.deliveryDate}` : ''}{form.deliveryTime ? ` ${form.deliveryTime}` : ''}</strong></div>
                <div className="qf-recap-row"><span>Format</span><strong>{DELIVERY_FORMATS.find((f) => f.id === form.deliveryFormat)?.label || '—'}</strong></div>
                <div className="qf-recap-row"><span>Mode de livraison</span><strong>{DELIVERY_MODES.find((m) => m.id === form.deliveryMode)?.label}{PHYSICAL_MODES.includes(form.deliveryMode) && form.recipientCity ? ` — ${form.recipientCity}, ${form.recipientCountry}` : ''}</strong></div>
                <div className="qf-recap-row"><span>Client</span><strong>{[form.civility, form.firstName, form.lastName].filter(Boolean).join(' ')}{form.email ? ` — ${form.email}` : ''}{form.phone ? `, ${form.phone}` : ''}{[form.city, form.country].filter(Boolean).length ? `, ${[form.city, form.country].filter(Boolean).join(', ')}` : ''}</strong></div>
                <div className="qf-recap-row"><span>Fichiers</span><strong>{form.files.map((f) => f.name).join(', ') || '—'}</strong></div>
                <div className="qf-recap-row"><span>Instructions</span><strong>{form.instructions || '—'}</strong></div>
              </div>

              <p className="qf-section">Prix</p>
              <table className="qf-price-table">
                <tbody>
                  {price.breakdown.map((item, i) => (
                    <tr key={i}>
                      <td>{item.label}</td>
                      <td>{formatTndWithEur(item.value)}</td>
                    </tr>
                  ))}
                  <tr className="qf-price-table-total">
                    <td>Total</td>
                    <td>{formatTndWithEur(price.total)}</td>
                  </tr>
                </tbody>
              </table>
              <small className="qf-hint">Le prix est calculé automatiquement selon les tarifs configurés par l&apos;administrateur.</small>

              {!isAuthenticated && (
                <div style={{ padding: 16, borderRadius: 14, background: 'var(--gray-50)', border: '1px solid var(--gray-100)', textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, margin: '0 0 6px' }}>Commande sans compte</p>
                  <p style={{ margin: '0', fontSize: '0.85rem' }}>
                    Un client sera créé avec vos coordonnées. Vous recevrez un email avec un lien de suivi sécurisé.
                    Vous pourrez créer votre compte plus tard et retrouver vos commandes.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 9 && form.category === 'interpreting' && (
            <div className="qf-stack">
              <p className="qf-section">Informations du client</p>
              <div className="qf-grid">
                {renderClientFields(!isAuthenticated)}
              </div>
            </div>
          )}

          {step === 10 && (
            <div className="qf-stack">
              <p className="qf-section">Récapitulatif</p>
              <div className="qf-stack" style={{ gap: 8, padding: 16, borderRadius: 14, background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Prestation</span><strong>{form.category === 'translation' ? 'Traduction' : 'Interprétariat'}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Type</span><strong>{form.category === 'translation' ? TRANSLATION_TYPES.find((t) => t.id === form.translationType)?.label : interpretingTypeOptions.find((t) => t.id === form.interpretingType)?.label}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Langue</span><strong>{form.langPairs.map((p) => `${p.source} → ${p.target}`).join(', ')}</strong></div>
                {form.category === 'interpreting' && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Type de mission</span><strong>{form.missionType}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Date</span><strong>{form.missionDate}{form.multiDay === 'yes' && form.endDate ? ` → ${form.endDate}` : ''}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Horaire</span><strong>{form.startTime}{form.endTime ? ` – ${form.endTime}` : ''}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Durée</span><strong>{form.duration ? `${form.duration} h` : '—'}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Mode</span><strong>{INTERPRETING_MODES.find((m) => m.id === form.interpretingMode)?.label}</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Lieu / Plateforme</span><strong>{missionPlace}</strong></div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Documents</span>
                  <strong>{[...form.files, ...form.prepFiles, ...form.programFiles, ...form.speakerFiles].map((f) => f.name).join(', ') || '—'}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Options</span>
                  <strong>
                    {[
                      ...form.officialServices.map((s) => OFFICIAL_SERVICES.find((o) => o.id === s)?.label).filter(Boolean),
                      ...(form.needsVerification !== 'none' ? ['Vérification professionnelle'] : []),
                    ].join(', ') || 'Aucune'}
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Prix total</span>
                  <strong>{price.total > 0 ? formatTndWithEur(price.total) : 'À déterminer'}</strong>
                </div>
              </div>

              <div style={{ marginTop: 18, padding: 16, borderRadius: 14, background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
                <p style={{ fontWeight: 700, margin: '0 0 8px' }}>Après l&apos;envoi</p>
                <ol style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem', lineHeight: 1.7 }}>
                  <li>Demande envoyée</li>
                  <li>Analyse par l&apos;Admin</li>
                  <li>Devis envoyé au client</li>
                  <li>Client accepte le devis</li>
                  <li>Paiement</li>
                  <li>Affectation du professionnel</li>
                  <li>Traduction / Interprétariat</li>
                  <li>Vérification Avocat / Notaire si nécessaire</li>
                  <li>Contrôle final Admin</li>
                  <li>Livraison au client</li>
                </ol>
              </div>

              <div style={{ padding: 16, borderRadius: 14, background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
                <p style={{ fontWeight: 700, margin: '0 0 8px' }}>Matching automatique</p>
                <p style={{ margin: '0 0 8px', fontSize: '0.85rem' }}>Le système associe votre demande au professionnel compatible :</p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem', lineHeight: 1.6 }}>
                  <li>Traduction : langue source + langue cible + domaine + type de traduction + type de document + services officiels + disponibilité</li>
                  <li>Interprète : langues + type d&apos;interprétariat + domaine + type de mission + mode + disponibilité</li>
                  <li>Vérification : langues + domaine + type de document + type d&apos;intervention + disponibilité</li>
                </ul>
                <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: 'var(--gray-500)' }}>L&apos;Admin conserve la possibilité de modifier ou confirmer l&apos;affectation proposée par le système.</p>
              </div>

              {!isAuthenticated && (
                <div style={{ padding: 16, borderRadius: 14, background: 'var(--gray-50)', border: '1px solid var(--gray-100)', textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, margin: '0 0 6px' }}>Commande sans compte</p>
                  <p style={{ margin: '0', fontSize: '0.85rem' }}>
                    Un client sera créé avec vos coordonnées. Vous recevrez un email avec un lien de suivi sécurisé.
                    Vous pourrez créer votre compte plus tard et retrouver vos commandes.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="qf-actions">
          {step > 1 ? (
            <button type="button" className="btn btn-outline" onClick={goPrev}>
              <ArrowLeft size={16} /> Précédent
            </button>
          ) : (
            <span />
          )}
          {step < totalSteps ? (
            <button type="button" className="btn btn-primary" onClick={goNext}>
              Suivant <ArrowRight size={16} />
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={submitting} onClick={handleSubmit}>
              {submitting ? 'Envoi en cours…' : 'Confirmer la commande et payer'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
